/**
 * Admin Context - Shared state and functions for admin panel
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { API } from "../../lib/api";

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error("useAdmin must be used within AdminProvider");
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [checkingAdmin, setCheckingAdmin] = useState(true);
  const [adminUser, setAdminUser] = useState(null);
  
  // Data state
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [donations, setDonations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [adoptionApplications, setAdoptionApplications] = useState([]);
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [googleCalendarStatus, setGoogleCalendarStatus] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [volunteerSchedules, setVolunteerSchedules] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [newsletterStats, setNewsletterStats] = useState(null);
  const [sentNewsletters, setSentNewsletters] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState([]);
  const [orders, setOrders] = useState([]);
  const [lostFoundReports, setLostFoundReports] = useState([]);
  const [lostFoundStats, setLostFoundStats] = useState(null);

  // Check admin status
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (authLoading) return;
      
      if (!user) {
        setCheckingAdmin(false);
        return;
      }
      
      if (user?.is_admin) {
        setAdminUser(user);
        setCheckingAdmin(false);
        return;
      }
      
      try {
        // SECURITY: Use httpOnly cookies for authentication (XSS immune)
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAdminUser(res.data);
      } catch (err) {
        console.error("Admin check failed:", err);
        setAdminUser(null);
      } finally {
        setCheckingAdmin(false);
      }
    };
    checkAdminStatus();
  }, [user, authLoading]);

  // Load all data
  const loadData = useCallback(async () => {
    // SECURITY: All requests use httpOnly cookies via withCredentials
    const config = { withCredentials: true };
    try {
      const [s, c, b, m, d, e, gcs, r, g, a, p] = await Promise.all([
        axios.get(`${API}/stats`, config),
        axios.get(`${API}/cats`, config),
        axios.get(`${API}/bookings/all`, config),
        axios.get(`${API}/contact/messages`, config),
        axios.get(`${API}/donations`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/calendar/events`, config).catch(() => ({ data: { events: [] } })),
        axios.get(`${API}/calendar/sync-status`, config).catch(() => ({ data: null })),
        axios.get(`${API}/reviews/all`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/gallery`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/adoptions`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/products`, config).catch(() => ({ data: [] }))
      ]);
      setStats(s.data);
      setCats(c.data);
      setBookings(b.data);
      setMessages(m.data);
      setDonations(d.data);
      setCalendarEvents(e.data.events || []);
      setGoogleCalendarStatus(gcs.data);
      setReviews(r.data);
      setGalleryImages(g.data);
      setAdoptionApplications(a.data);
      setProducts(p.data || []);
      
      // Load additional data for new tabs
      const [blog, vol, volSched, subs, nlStats, sent, anal, trendData, ord, lfReports, lfStats] = await Promise.all([
        axios.get(`${API}/blog?published_only=false`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/volunteers`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/volunteers/schedules`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/newsletter/subscribers`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/newsletter/stats`, config).catch(() => ({ data: null })),
        axios.get(`${API}/newsletter/sent`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/analytics/dashboard`, config).catch(() => ({ data: null })),
        axios.get(`${API}/analytics/trends`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/orders`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/lost-found`, config).catch(() => ({ data: [] })),
        axios.get(`${API}/lost-found/stats/overview`, config).catch(() => ({ data: null }))
      ]);
      setBlogPosts(blog.data || []);
      setVolunteers(vol.data || []);
      setVolunteerSchedules(volSched.data || []);
      setSubscribers(subs.data || []);
      setNewsletterStats(nlStats.data);
      setSentNewsletters(sent.data || []);
      setAnalytics(anal.data);
      setTrends(trendData.data || []);
      setOrders(ord.data || []);
      setLostFoundReports(lfReports.data || []);
      setLostFoundStats(lfStats.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data when admin is confirmed
  useEffect(() => {
    if (adminUser?.is_admin && !loading) {
      loadData();
    } else if (adminUser?.is_admin) {
      loadData();
    }
  }, [adminUser, loadData]);

  const value = {
    // Auth
    user,
    adminUser,
    checkingAdmin,
    loading,
    
    // Data
    stats,
    cats, setCats,
    bookings, setBookings,
    messages, setMessages,
    donations,
    reviews, setReviews,
    adoptionApplications, setAdoptionApplications,
    calendarEvents, setCalendarEvents,
    googleCalendarStatus, setGoogleCalendarStatus,
    galleryImages, setGalleryImages,
    products, setProducts,
    blogPosts, setBlogPosts,
    volunteers, setVolunteers,
    volunteerSchedules, setVolunteerSchedules,
    subscribers, setSubscribers,
    newsletterStats,
    sentNewsletters,
    analytics,
    trends,
    orders, setOrders,
    lostFoundReports, setLostFoundReports,
    lostFoundStats,
    
    // Actions
    loadData,
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};
